import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-hot-toast';

const KatifiWebStudioTab = () => {
    const [domains, setDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState('');
    
    // Form States
    const [colorScheme, setColorScheme] = useState('Modern Tailwind, Custom Gradients');
    const [pagesToBuild, setPagesToBuild] = useState({ home: true, about: false, contact: false, services: false });
    const [numBlogs, setNumBlogs] = useState(0);
    const [lawyerBio, setLawyerBio] = useState(false);
    const [addAnalytics, setAddAnalytics] = useState(false);
    const [targetLocation, setTargetLocation] = useState('');
    const [contentPrompt, setContentPrompt] = useState('');
    
    // SSH Execution States
    const [executionMode, setExecutionMode] = useState('api'); // 'api' or 'ssh'
    const [sshConfig, setSshConfig] = useState({ host: '', port: 22, username: 'root', password: '', privateKey: '' });

    // Execution / Terminal States
    const [isExecuting, setIsExecuting] = useState(false);
    const [terminalLogs, setTerminalLogs] = useState([]);
    const [completedPreview, setCompletedPreview] = useState(null);
    const [manusSessionId, setManusSessionId] = useState(`session_${Date.now()}`);
    const terminalEndRef = useRef(null);

    useEffect(() => {
        const fetchDomains = async () => {
            try {
                const data = await apiService.makeRequest('/organic-seo/domains');
                const domainList = Array.isArray(data) ? data : (data.domains || []);
                
                setDomains(domainList);
                
                const urlParams = new URLSearchParams(window.location.search);
                const urlDomainId = urlParams.get('domain'); // This is usually a raw URL string from the earlier component.
                
                let matchedDomain = '';
                if (urlDomainId) {
                    const cleanUrlParam = urlDomainId.replace(/^https?:\/\//, '').replace(/\/$/, '');
                    
                    // Allow exact match manually typed if it doesn't exist, but test if it's in the list
                    const exactMatch = domainList.find(d => {
                        const dStr = typeof d === 'string' ? d : (d.domain || d.url || '');
                        return dStr.includes(cleanUrlParam) || cleanUrlParam.includes(dStr);
                    });
                    
                    if (exactMatch) {
                        matchedDomain = typeof exactMatch === 'string' ? exactMatch : (exactMatch.domain || exactMatch.url);
                    } else {
                        // If it came from the URL, just set it as the value anyway!
                        matchedDomain = cleanUrlParam;
                    }
                }

                if (matchedDomain) {
                    setSelectedDomain(matchedDomain);
                } else if (domainList.length > 0) {
                    const first = domainList[0];
                    setSelectedDomain(typeof first === 'string' ? first : (first.domain || first.url));
                }
            } catch (err) {
                console.error("Failed to fetch domains", err);
            }
        };
        fetchDomains();
    }, []);

    useEffect(() => {
        if (terminalEndRef.current) {
            terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [terminalLogs]);

    const handleExecuteManus = async () => {
        if (!selectedDomain) {
            toast.error("Please select a domain target.");
            return;
        }

        const pagesContext = [];
        if (pagesToBuild.home) pagesContext.push("Лендинг/Главная страница");
        if (pagesToBuild.about) pagesContext.push("Страница 'О нас'");
        if (pagesToBuild.contact) pagesContext.push("Контакты");
        if (pagesToBuild.services) pagesContext.push("Услуги");

        let commandPrompt = `Создай или полностью переделай структуру и дизайн для сайта https://${selectedDomain}.\n`;
        commandPrompt += `Необходимо создать следующие страницы: ${pagesContext.join(', ')}.\n`;
        
        if (numBlogs > 0) {
            commandPrompt += `Также сгенерируй структуру и контент для ${numBlogs} SEO-статей для блога.\n`;
        }
        if (lawyerBio) {
            commandPrompt += `Добавь детализированный блок или отдельную страницу для Био Адвоката.\n`;
        }
        if (addAnalytics) {
            commandPrompt += `Интегрируй заглушки или скрипты для Аналитики (Google Analytics/Tag Manager).\n`;
        }
        if (targetLocation) {
            commandPrompt += `Оптимизируй контент и метатеги локально под этот город/локацию: ${targetLocation}.\n`;
        }
        
        commandPrompt += `\nТребования к дизайну: Используй ${colorScheme}. Сделай стильно, современно, картинки только реальные из Unsplash.\n`;
        
        if (contentPrompt) {
            commandPrompt += `\nДополнительные требования к текстам: ${contentPrompt}`;
        }

        setIsExecuting(true);
        setTerminalLogs([{ step: 'Initialization', details: `Formulating architecture for ${selectedDomain}...` }]);
        setCompletedPreview(null);
        
        const newSessionId = `session_${Date.now()}`;
        setManusSessionId(newSessionId);

        try {
            if (executionMode === 'ssh') {
                setTerminalLogs([{ step: 'Initialization', details: `Connecting to ${sshConfig.host} via SSH Mode...` }]);

                const data = await apiService.makeRequest('/admin/katifi/ssh-execute', {
                    method: 'POST',
                    body: JSON.stringify({
                        domain: selectedDomain,
                        host: sshConfig.host,
                        port: sshConfig.port,
                        username: sshConfig.username,
                        password: sshConfig.password,
                        private_key: sshConfig.privateKey
                    })
                });

                setTerminalLogs(prev => [...prev, { step: 'Celery Queued', details: `Task ID: ${data.task_id}\nBackground operation started. Katifi will zip the files, redesign, and update them.` }]);
                setIsExecuting(false);
                return;
            }

            // API Mode Logic (SSE streaming - use fetch with credentials)
            const response = await fetch('/api/v1/admin/katifi/manus/execute', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ command: commandPrompt, session_id: newSessionId })
            });

            if (!response.ok) throw new Error(`Manus Error: ${response.status}`);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let isComplete = false;
            
            while (!isComplete) {
                const { value, done } = await reader.read();
                if (done) {
                    if (!isComplete) {
                        setTerminalLogs(prev => [...prev, { step: 'Error', details: 'Stream disconnected prematurely.' }]);
                    }
                    isComplete = true;
                    break;
                }
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.substring(6).trim();
                        if (!dataStr) continue;
                        try {
                            const data = JSON.parse(dataStr);
                            if (data.type === 'complete') {
                                isComplete = true;
                                const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
                                const pUrl = data.url ? (baseUrl.replace('/api/v1', '') + data.url) : null;
                                const sUrl = data.screenshot ? (baseUrl.replace('/api/v1', '') + data.screenshot) : null;
                                setCompletedPreview({ url: pUrl, screenshot: sUrl });
                            } else if (data.type === 'log') {
                                setTerminalLogs(prev => [...prev, { step: data.step, details: data.details }]);
                            } else if (data.type === 'error') {
                                setTerminalLogs(prev => [...prev, { step: 'Fatal Error', details: data.details }]);
                                isComplete = true;
                            }
                        } catch (e) {}
                    }
                }
            }
        } catch (err) {
            setTerminalLogs(prev => [...prev, { step: 'Server Error', details: err.message }]);
        } finally {
            setIsExecuting(false);
        }
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div>
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        🎨 Katifi Web Studio
                        <span style={{ fontSize: '12px', background: '#3b82f6', color: '#fff', padding: '2px 8px', borderRadius: '12px' }}>BETA</span>
                    </h2>
                    <p style={{ color: '#64748b', margin: 0 }}>Configure and monitor autonomous web design tasks via Katifi-Design Agent.</p>
                </div>
                
                <div style={{ display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '4px' }}>
                    <button 
                        onClick={() => setExecutionMode('api')}
                        style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: executionMode === 'api' ? '#fff' : 'transparent', fontWeight: executionMode === 'api' ? 'bold' : 'normal', color: executionMode === 'api' ? '#2563eb' : '#64748b', cursor: 'pointer', boxShadow: executionMode === 'api' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                    >API Sandbox Mode</button>
                    <button 
                        onClick={() => setExecutionMode('ssh')}
                        style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: executionMode === 'ssh' ? '#fff' : 'transparent', fontWeight: executionMode === 'ssh' ? 'bold' : 'normal', color: executionMode === 'ssh' ? '#dc2626' : '#64748b', cursor: 'pointer', boxShadow: executionMode === 'ssh' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                    >⚠️ Native SSH Redesign (Live)</button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                {/* FORM COLUMN */}
                <div style={{ flex: '1 1 500px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                        1. Target Blueprint
                    </h3>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Target Domain / Project URL (Any CMS)</label>
                        <input 
                            list="domain-options"
                            value={selectedDomain} 
                            onChange={(e) => setSelectedDomain(e.target.value)}
                            placeholder="e.g. example.com or select from list..."
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                        <datalist id="domain-options">
                            {domains.map((d, i) => {
                                const val = typeof d === 'string' ? d : (d.domain || d.url);
                                return val ? <option key={i} value={val} /> : null;
                            })}
                        </datalist>
                        
                        {selectedDomain && domains.some(d => {
                            const val = typeof d === 'string' ? d : (d.domain || d.url);
                            return val === selectedDomain || (val && val.includes(selectedDomain));
                        }) && (
                            <div style={{ marginTop: '8px', fontSize: '13px' }}>
                                <a href={`/admin/brain?tab=phoenix&domain=${selectedDomain}`} style={{ color: '#0ea5e9', textDecoration: 'underline' }}>
                                    → Go to WP <strong>{selectedDomain}</strong> Organic SEO Command Center
                                </a>
                            </div>
                        )}
                        {selectedDomain && !domains.some(d => {
                            const val = typeof d === 'string' ? d : (d.domain || d.url);
                            return val === selectedDomain || (val && val.includes(selectedDomain));
                        }) && (
                            <div style={{ marginTop: '8px', fontSize: '13px', color: '#64748b' }}>
                                ℹ️ External Domain detected. Katifi Manus will build the architecture independently.
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>CSS Framework & Vibe</label>
                        <input 
                            type="text" 
                            value={colorScheme} 
                            onChange={(e) => setColorScheme(e.target.value)}
                            placeholder="e.g. Dark Mode, Gold Gradients, Tailwind"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Architecture Map</label>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                                <input type="checkbox" checked={pagesToBuild.home} onChange={e => setPagesToBuild({...pagesToBuild, home: e.target.checked})} />
                                Home Landing
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                                <input type="checkbox" checked={pagesToBuild.about} onChange={e => setPagesToBuild({...pagesToBuild, about: e.target.checked})} />
                                About Us
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                                <input type="checkbox" checked={pagesToBuild.contact} onChange={e => setPagesToBuild({...pagesToBuild, contact: e.target.checked})} />
                                Contact Form
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                                <input type="checkbox" checked={pagesToBuild.services} onChange={e => setPagesToBuild({...pagesToBuild, services: e.target.checked})} />
                                Practice Areas
                            </label>
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Number of SEO Blogs</label>
                        <input 
                            type="number" 
                            min="0" max="100" 
                            value={numBlogs} 
                            onChange={(e) => setNumBlogs(parseInt(e.target.value) || 0)}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ fontWeight: '500', color: '#334155', margin: 0 }}>SEO Target Location (City/State)</label>
                            <button type="button" onClick={() => setTargetLocation("New York, NY")} style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>⚡ Auto</button>
                        </div>
                        <input 
                            type="text" 
                            value={targetLocation} 
                            onChange={(e) => setTargetLocation(e.target.value)}
                            placeholder="e.g. New York, Brooklyn, Queens"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ fontWeight: '500', color: '#334155', margin: 0 }}>AI Content Persona / Custom Prompt</label>
                            <button type="button" onClick={() => setContentPrompt("Act as a top-tier luxury law firm. Focus on exclusive, high-end legal services. The tone should be authoritative, empathetic, and premium. Highlight our decades of experience and deep knowledge of New York law. Keep content highly professional but persuasive.")} style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>⚡ Auto</button>
                        </div>
                        <textarea 
                            value={contentPrompt} 
                            onChange={(e) => setContentPrompt(e.target.value)}
                            placeholder="Describe how the content should sound. e.g. 'Aggressive trial lawyer tone, emphasize millions won, no censorship...'"
                            rows="4"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                        ></textarea>
                    </div>

                    {executionMode === 'ssh' && (
                        <div style={{ marginBottom: '24px', padding: '15px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '12px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#b91c1c' }}>🔑 SSH Authentication (Danger Zone)</h4>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input type="text" placeholder="Server IP or Host" value={sshConfig.host} onChange={(e) => setSshConfig({...sshConfig, host: e.target.value})} style={{ flex: 2, padding: '8px', borderRadius: '6px', border: '1px solid #f87171' }} />
                                <input type="number" placeholder="Port" value={sshConfig.port} onChange={(e) => setSshConfig({...sshConfig, port: e.target.value})} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #f87171' }} />
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input type="text" placeholder="Username (root)" value={sshConfig.username} onChange={(e) => setSshConfig({...sshConfig, username: e.target.value})} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #f87171' }} />
                                <input type="password" placeholder="Password (Optional if Key)" value={sshConfig.password} onChange={(e) => setSshConfig({...sshConfig, password: e.target.value})} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #f87171' }} />
                            </div>
                            <textarea placeholder="Private RSA Key (Optional if Pass)" value={sshConfig.privateKey} onChange={(e) => setSshConfig({...sshConfig, privateKey: e.target.value})} rows="2" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #f87171' }}></textarea>
                        </div>
                    )}

                    <button 
                        onClick={handleExecuteManus}
                        disabled={isExecuting || !selectedDomain}
                        style={{ 
                            width: '100%', padding: '14px', borderRadius: '8px', 
                            background: isExecuting || !selectedDomain ? '#94a3b8' : '#2563eb', 
                            color: '#fff', fontSize: '16px', fontWeight: 'bold', 
                            border: 'none', cursor: isExecuting || !selectedDomain ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
                        }}
                    >
                        {isExecuting ? '⏳ Katifi-Design is Building...' : (executionMode === 'ssh' ? '🚀 Execute Autonomous Remote Redesign' : '🚀 Launch Katifi-Design Engine (Sandbox)')}
                    </button>
                    <p style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '10px' }}>
                        {executionMode === 'ssh' ? 'Katifi-Design will SSH into the target host, backup the files as a ZIP, and rewrite them in-place with top UI designs.' : 'This will generate the HTML architecture in the AI Sandbox. Direct deployment is handled post-verification.'}
                    </p>
                </div>

                {/* TERMINAL & RESULTS COLUMN */}
                <div style={{ flex: '1 1 500px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    <div style={{ flex: 1, background: '#0f172a', borderRadius: '12px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: '10px 15px', background: '#1e293b', borderBottom: '1px solid #334155', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></div>
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }}></div>
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }}></div>
                            <span style={{ marginLeft: '10px', color: '#94a3b8', fontSize: '12px', fontFamily: 'monospace' }}>Katifi-Design execution engine</span>
                        </div>
                        
                        <div style={{ padding: '16px', overflowY: 'auto', minHeight: '300px', maxHeight: '500px', color: '#a5b4fc', fontFamily: '"Fira Code", monospace', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {terminalLogs.length === 0 && !isExecuting && (
                                <div style={{ color: '#475569', fontStyle: 'italic', textAlign: 'center', marginTop: '40px' }}>
                                    Awaiting architecture instructions...
                                </div>
                            )}
                            {terminalLogs.map((log, i) => (
                                <div key={i} style={{ display: 'flex', gap: '10px' }}>
                                    <span style={{ color: '#94a3b8' }}>[{new Date().toLocaleTimeString()}]</span>
                                    <span style={{ color: '#38bdf8', fontWeight: 'bold' }}>[{log.step}]</span>
                                    <span style={{ color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>{log.details}</span>
                                </div>
                            ))}
                            {isExecuting && (
                                <div style={{ color: '#22c55e', animation: 'pulse 1.5s infinite' }}>
                                    _
                                </div>
                            )}
                            <div ref={terminalEndRef} />
                        </div>
                    </div>

                    {completedPreview && (
                        <div style={{ background: '#ecfeff', border: '1px solid #67e8f9', borderRadius: '12px', padding: '20px' }}>
                            <h3 style={{ marginTop: 0, color: '#0891b2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ✅ Architecture Sandbox Complete
                            </h3>
                            <div style={{ marginBottom: '15px' }}>
                                <a href={completedPreview.url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', padding: '10px 16px', background: '#0284c7', color: '#fff', textDecoration: 'none', borderRadius: '6px', fontWeight: '600' }}>
                                    🔗 Open Sandbox Preview
                                </a>
                            </div>
                            {completedPreview.screenshot && (
                                <div>
                                    <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#0f766e', fontWeight: '500' }}>Live Verification Screenshot:</p>
                                    <img src={completedPreview.screenshot} alt="Generated Preview" style={{ width: '100%', borderRadius: '8px', border: '1px solid #bae6fd' }} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0; }
                    100% { opacity: 1; }
                }
            `}</style>
        </div>
    );
};

export default KatifiWebStudioTab;
